import type React from 'react';
import { toast } from 'react-toastify';
import { makeUtilityAccountLinkForProjectId } from '/imports/api/utilityAccounts';
import { copyToClipboard } from '/imports/utils';
import { strings } from '/imports/strings';

/**
 * Wraps an async function so thrown errors surface as a toast.
 * @param func - the async function to guard
 * @param message - optional override for the error toast text
 * @param fin - optional callback run in finally
 * @returns a wrapped function with the same arguments
 */
export const toastOnError = <T, A extends any[] = []>(func: (...a: A) => Promise<T>, message?: string, fin?: () => void | Promise<void>) => {
  return async (...a: A) => {
    try {
      return await func(...a);
    }
    catch (error: any) {
      console.log(error);
      toast.error(message || error.message || error.reason || error.error || `${error}`);
    }
    finally {
      await fin?.();
    }
  };
};

/**
 * Copies the utility-account credentials link for a project to the clipboard.
 * @param projectId - the project to generate the link for
 */
export const copyUtilityCredentialsLink = toastOnError(async (projectId: string) => {
  const link = await makeUtilityAccountLinkForProjectId.call(projectId);
  copyToClipboard(link);
  toast.success(strings.CopiedToClipboard());
});

/**
 * Whether a drag carries files, as opposed to selected text or a dragged link. Checked before a drop
 * target lights up, so dragging a text selection across the page does not make every uploader glow.
 * @param e - the drag event
 * @returns true when the payload includes files
 */
export const isFileDrag = (e: React.DragEvent): boolean =>
  Array.from(e.dataTransfer?.types || []).includes('Files');

/**
 * Whether a dragleave is the pointer actually leaving the element, rather than crossing onto one of its
 * own children — which fires dragleave on the parent and would otherwise flicker the highlight off.
 * @param e - the dragleave event
 * @returns true when the pointer has left the element entirely
 */
export const isDragLeaving = (e: React.DragEvent): boolean =>
  !e.relatedTarget || !(e.currentTarget as Node).contains(e.relatedTarget as Node);
